import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { TileArrow } from './TileHeader.styles'
import { blue, black, white } from '@/theme/colors'
import { shadows, radii, media, motion } from '@/theme/tokens'
import { text } from '@/theme/typography'

interface SurfaceProps {
	$colSpan: number
	$rowSpan?: number
}

export const TileBody = styled.div`
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;

	& > * {
		flex: 1;
	}
`

export const TileFooter = styled.div`
	${text.label}
	color: ${blue};
	margin-top: auto;
	padding-top: 4px;
`

/**
 * Shared bento-tile surface: a card that lifts on hover and reveals its header
 * arrow. Applied to both the `<button>` and `<Link>` variants so they are
 * pixel-identical. Grid spans arrive as transient props; below 980px every tile
 * collapses to full width.
 */
const surface = css<SurfaceProps>`
	grid-column: span ${({ $colSpan }) => $colSpan};
	${({ $rowSpan }) => ($rowSpan ? `grid-row: span ${$rowSpan};` : '')}
	display: flex;
	flex-direction: column;
	gap: 18px;
	background: ${white};
	border: none;
	border-radius: ${radii.card};
	box-shadow: ${shadows.card};
	padding: 28px;
	text-align: left;
	text-decoration: none;
	color: ${black};
	cursor: pointer;
	${text.body}
	min-width: 0;
	transition:
		box-shadow ${motion.base},
		transform ${motion.base};

	&:hover {
		box-shadow: ${shadows.cardHover};
		transform: translateY(-2px);
	}

	&:hover ${TileArrow} {
		opacity: 1;
		transform: translateX(0);
	}

	&:active {
		transform: scale(0.98);
	}

	&:focus-visible {
		outline: 2px solid ${blue};
		outline-offset: 2px;
	}

	/* The stack: full-width cards with the handoff's 20px padding / 16px inner gap. */
	${media.bentoCollapse} {
		grid-column: span 12;
		grid-row: auto;
		gap: 16px;
		padding: 20px;
	}

	/*
	 * Touch devices get a press-scale only — no hover lift (which sticks after a
	 * tap) — and the header arrow stays visible since there is no hover to reveal
	 * it (issue 08: hover-only affordances need touch equivalents).
	 */
	@media (hover: none) {
		&:hover {
			box-shadow: ${shadows.card};
			transform: none;
		}

		${TileArrow} {
			opacity: 1;
			transform: none;
		}
	}
`

export const TileButton = styled.button<SurfaceProps>`
	${surface}
`

export const TileLink = styled(Link)<SurfaceProps>`
	${surface}
`
